import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterData } from '../../types';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import { validateDocument, formatDocument } from '../../utils/documentValidation';
import { Eye, EyeOff, Compass, UserPlus, Shield, CheckCircle } from 'lucide-react';

const registerSchema = yup.object().shape({
  name: yup
    .string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .required('Nome é obrigatório'),
  email: yup
    .string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório'),
  nationality: yup
    .string()
    .required('Nacionalidade é obrigatória'),
  document_type: yup
    .string()
    .required('Tipo de documento é obrigatório'),
  document_number: yup
    .string()
    .test('document-validation', 'Documento inválido', function(value) {
      const { document_type } = this.parent;
      if (!value || !document_type) return false;
      return validateDocument(value, document_type);
    })
    .required('Número do documento é obrigatório'),
  password: yup
    .string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref('password')], 'Senhas não coincidem')
    .required('Confirmação de senha é obrigatória'),
});

const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register: registerUser, isLoading } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterData & {
    nationality: string;
    document_type: string;
    document_number: string;
  }>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      nationality: 'BR',
      document_type: 'CPF'
    }
  });

  const watchedNationality = watch('nationality');
  const watchedDocumentType = watch('document_type');
  const onSubmit = async (data: RegisterData) => {
    try {
      setError(null);
      await registerUser(data);
      // Redirecionar para login após registro bem-sucedido
      navigate('/login', { 
        state: { 
          message: 'Conta criada com sucesso! Faça login para continuar.',
          email: data.email 
        }
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Register Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12 bg-white">
        <div className="max-w-md w-full space-y-8">
          {/* Logo and Header */}
          <div className="text-center">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl mb-4 shadow-lg">
                <UserPlus className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">
              Criar Conta
            </h1>
            <p className="text-gray-600 text-lg">
              Junte-se ao portal do turista Jeri Digital
            </p>
          </div>

          {/* Register Form */}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <Alert type="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome completo
                </label>
                <input
                  {...register('name')}
                  type="text"
                  autoComplete="name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                  placeholder="Digite seu nome completo"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  E-mail
                </label>
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                  placeholder="Digite seu e-mail"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="nationality" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nacionalidade
                </label>
                <select
                  {...register('nationality')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900"
                >
                  <option value="">Selecione sua nacionalidade</option>
                  <option value="BR">🇧🇷 Brasil</option>
                  <option value="US">🇺🇸 Estados Unidos</option>
                  <option value="GB">🇬🇧 Reino Unido</option>
                  <option value="FR">🇫🇷 França</option>
                  <option value="DE">🇩🇪 Alemanha</option>
                  <option value="ES">🇪🇸 Espanha</option>
                  <option value="IT">🇮🇹 Itália</option>
                  <option value="PT">🇵🇹 Portugal</option>
                  <option value="AR">🇦🇷 Argentina</option>
                  <option value="CL">🇨🇱 Chile</option>
                  <option value="CO">🇨🇴 Colômbia</option>
                  <option value="PE">🇵🇪 Peru</option>
                  <option value="UY">🇺🇾 Uruguai</option>
                  <option value="PY">🇵🇾 Paraguai</option>
                  <option value="BO">🇧🇴 Bolívia</option>
                  <option value="EC">🇪🇨 Equador</option>
                  <option value="VE">🇻🇪 Venezuela</option>
                  <option value="CA">🇨🇦 Canadá</option>
                  <option value="MX">🇲🇽 México</option>
                  <option value="JP">🇯🇵 Japão</option>
                  <option value="CN">🇨🇳 China</option>
                  <option value="IN">🇮🇳 Índia</option>
                  <option value="AU">🇦🇺 Austrália</option>
                  <option value="OTHER">🌐 Outro</option>
                </select>
                {errors.nationality && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                    {errors.nationality.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="document_type" className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Documento
                  </label>
                  <select
                    {...register('document_type')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900"
                  >
                    <option value="CPF">CPF</option>
                    <option value="Passaporte">Passaporte</option>
                    <option value="Outro">Outro</option>
                  </select>
                  {errors.document_type && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                      {errors.document_type.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="document_number" className="block text-sm font-semibold text-gray-700 mb-2">
                    {watchedDocumentType === 'CPF' ? 'CPF' : 'Número do Documento'}
                  </label>
                  <input
                    {...register('document_number')}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    placeholder={watchedDocumentType === 'CPF' ? '000.000.000-00' : 'Digite o número do documento'}
                    onChange={(e) => {
                      const formatted = formatDocument(e.target.value, watchedDocumentType);
                      e.target.value = formatted;
                      register('document_number').onChange(e);
                    }}
                  />
                  {errors.document_number && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                      {errors.document_number.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password_confirmation" className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirmar senha
                </label>
                <div className="relative">
                  <input
                    {...register('password_confirmation')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    placeholder="Confirme sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password_confirmation && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                    {errors.password_confirmation.message}
                  </p>
                )}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] shadow-lg"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" showText={false} />
                    <span className="ml-2">Criando conta...</span>
                  </>
                ) : (
                  'Criar conta'
                )}
              </button>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-gray-600">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-emerald-600 hover:text-emerald-500 transition-colors"
                >
                  Faça login aqui
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Visual/Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-emerald-600 via-green-700 to-teal-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }} />
        </div>
        
        {/* Content */}
        <div className="relative flex flex-col justify-center px-12">
          <div className="text-white">
            <h2 className="text-4xl font-bold mb-6">
              Sua Jornada Começa Aqui! 🌟
            </h2>
            <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
              Crie sua conta e tenha acesso completo ao portal do turista. 
              Organize viagens inesquecíveis com facilidade e segurança.
            </p>
            
            {/* Benefits */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Acesso Completo</h3>
                  <p className="text-emerald-100">Todas as funcionalidades do portal</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Dados Seguros</h3>
                  <p className="text-emerald-100">Suas informações protegidas</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                  <Compass className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Experiência Personalizada</h3>
                  <p className="text-emerald-100">Portal adaptado ao seu perfil</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full animate-pulse delay-300"></div>
        <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full animate-pulse delay-700"></div>
      </div>
    </div>
  );
};

export default RegisterPage;
